import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react'
import api from 'common/services/api'
import { TeacherInstance } from 'common/utils/types/account-types'
import { ClientTeacher } from 'common/utils/types/client-teacher'
import {
  ClientClassroomInstance,
  ClientStudentInstance,
  ClientTeacherInstance,
} from 'common/utils/types/account-types-client'
import { ClientClassroom } from 'common/utils/types/client-classroom'
import { ClientStudent } from 'common/utils/types/client-student'
import useGetCurrentUser from 'common/hooks/useGetCurrentUser'

interface TeacherContextValue {
  currentTeacher: ClientTeacherInstance | null
  classrooms: Record<string, any> | null
  students: ClientStudentInstance[] | null
  studentsPerClassroom: Record<string, ClientStudentInstance[]> | null
  updateTeacher: (prop: keyof TeacherInstance, value: any) => void
  refreshStudents: () => void
  ensureStudentsAndClassroom: (
    studentCodes: string[],
    classroomCode: string
  ) => Promise<void>
  fetchKids: () => void
}

const TeacherContext = createContext<TeacherContextValue | null>(null)

export function TeacherProvider({ children }: { children: React.ReactNode }) {
  const { currentTeacher: teacherFromHook, fetchTeacher } = useGetCurrentUser()
  const [internalTeacherData, setInternalTeacherData] =
    useState<TeacherInstance | null>(null)

  useEffect(() => {
    setInternalTeacherData(teacherFromHook)
  }, [teacherFromHook])

  const clientTeacher = useMemo(() => {
    if (!internalTeacherData) return null
    return new ClientTeacher(internalTeacherData)
  }, [internalTeacherData])

  const classroomCodes = useMemo(() => {
    return clientTeacher?.classroomCodes(null) ?? null
  }, [clientTeacher])

  const [classrooms, setClassrooms] = useState<Record<string, any> | null>(null)
  const [students, setStudents] = useState<ClientStudentInstance[] | null>(null)
  const [studentCodes, setStudentCodes] = useState<Record<string, string>>({})
  const [studentsPerClassroom, setStudentsPerClassroom] = useState<Record<
    string,
    ClientStudentInstance[]
  > | null>(null)

  useEffect(() => {
    if (!classroomCodes) return
    api({
      url: '/api2/refresh',
      method: 'POST',
      data: {
        teachers: {},
        classrooms: Object.keys(classroomCodes).reduce((acc, code) => {
          acc[code] = { version: 0, secret: classroomCodes[code] }
          return acc
        }, {}),
        students: {},
      },
    })
      .then((response) => {
        setClassrooms(response.data.classrooms.items)
      })
      .catch((err) => console.error(err))
  }, [classroomCodes])

  useEffect(() => {
    if (!classrooms) return
    const allStudentCodes = {}
    Object.keys(classrooms).forEach((classroomCode) => {
      const classroomData = classrooms[classroomCode]
      if (classroomData) {
        const c: ClientClassroomInstance = new ClientClassroom(classroomData)
        Object.assign(allStudentCodes, c.studentCodes())
      }
    })
    setStudentCodes(allStudentCodes)
  }, [classrooms])

  const refreshStudents = useCallback(() => {
    if (Object.keys(studentCodes).length === 0 || !classrooms) return
    api({
      url: '/api2/refresh',
      method: 'POST',
      data: {
        teachers: {},
        students: Object.keys(studentCodes).reduce((acc, code) => {
          acc[code] = { version: 0, secret: studentCodes[code] }
          return acc
        }, {}),
        classrooms: {},
      },
    })
      .then((response) => {
        setStudents(
          Object.keys(response.data.students.items).map(
            (s) => new ClientStudent(response.data.students.items[s])
          )
        )
        Object.values(classrooms).forEach((classroom) => {
          const c: ClientClassroomInstance = new ClientClassroom(classroom)
          const classroomStudents = Object.keys(c.studentCodes() || {}).map(
            (s) => new ClientStudent(response.data.students.items[s])
          )
          setStudentsPerClassroom((prev) => ({
            ...prev,
            [classroom.code]: classroomStudents,
          }))
        })
      })
      .catch((err) => console.error(err))
  }, [studentCodes, classrooms])

  const fetchKids = useCallback(() => {
    if (!clientTeacher?.isParent) return
    const childCodes = clientTeacher?.childCodes() || {}
    api({
      url: '/api2/refresh',
      method: 'POST',
      data: {
        teachers: {},
        students: Object.keys(childCodes).reduce((acc, code) => {
          acc[code] = { version: 0, secret: childCodes[code] }
          return acc
        }, {}),
        classrooms: {},
      },
    })
      .then((response) => {
        setStudentsPerClassroom((prev) => ({
          ...prev,
          my_kids: Object.keys(response.data.students.items).map(
            (s) => new ClientStudent(response.data.students.items[s])
          ),
        }))
      })
      .catch((err) => console.error(err))
  }, [clientTeacher])

  const processClassroomData = useCallback(
    (
      classroomData: any,
      classroomCode: string,
      missingStudentCodes: string[]
    ) => {
      const c: ClientClassroomInstance = new ClientClassroom(classroomData)
      const classroomStudentCodes = c.studentCodes() || {}
      setStudentCodes((prev) => ({
        ...(prev || {}),
        ...classroomStudentCodes,
      }))

      const studentsToRefresh = missingStudentCodes.filter(
        (code) => classroomStudentCodes[code]
      )

      return { classroomStudentCodes, studentsToRefresh }
    },
    []
  )

  const refreshAndUpdateStudents = useCallback(
    async (
      studentsToRefresh: string[],
      classroomStudentCodes: Record<string, string>,
      classroomCode: string
    ) => {
      if (studentsToRefresh.length === 0) {
        const classroomStudents = Object.keys(classroomStudentCodes)
          .map((code) => students?.find((s) => s.code === code))
          .filter((s): s is ClientStudentInstance => !!s)
        setStudentsPerClassroom((prev) => ({
          ...(prev || {}),
          [classroomCode]: classroomStudents,
        }))
        return
      }

      const studentsResponse = await api({
        url: '/api2/refresh',
        method: 'POST',
        data: {
          teachers: {},
          students: studentsToRefresh.reduce((acc, code) => {
            acc[code] = {
              version: 0,
              secret: classroomStudentCodes[code],
            }
            return acc
          }, {}),
          classrooms: {},
        },
      })

      const refreshedStudents = studentsResponse.data.students.items
      setStudents((prev) => {
        const existing = prev || []
        const newStudents = Object.keys(refreshedStudents).map(
          (s) => new ClientStudent(refreshedStudents[s])
        )
        const existingCodes = new Set(existing.map((s) => s.code))
        const uniqueNew = newStudents.filter((s) => !existingCodes.has(s.code))
        return [...existing, ...uniqueNew]
      })

      const classroomStudents = Object.keys(classroomStudentCodes)
        .map((code) => {
          if (refreshedStudents[code]) {
            return new ClientStudent(refreshedStudents[code])
          }
          return students?.find((s) => s.code === code)
        })
        .filter((s): s is ClientStudentInstance => !!s)
      setStudentsPerClassroom((prev) => ({
        ...(prev || {}),
        [classroomCode]: classroomStudents,
      }))
    },
    [students]
  )

  const refreshClassroom = useCallback(
    async (classroomCode: string, secret: string) => {
      const classroomResponse = await api({
        url: '/api2/refresh',
        method: 'POST',
        data: {
          teachers: {},
          classrooms: {
            [classroomCode]: {
              version: 0,
              secret,
            },
          },
          students: {},
        },
      })

      const refreshedClassrooms = classroomResponse.data.classrooms.items
      setClassrooms((prev) => ({
        ...(prev || {}),
        ...refreshedClassrooms,
      }))

      return refreshedClassrooms[classroomCode]
    },
    []
  )

  const ensureStudentsAndClassroom = useCallback(
    async (
      requestedStudentCodes: string[],
      classroomCode: string
    ): Promise<void> => {
      if (!classroomCodes) return

      const isNewClassroom = !classroomCodes[classroomCode]
      let needsClassroomRefresh = !classrooms || !classrooms[classroomCode]

      if (!needsClassroomRefresh && classrooms?.[classroomCode]) {
        const c = new ClientClassroom(classrooms[classroomCode])
        const currentCodes = Object.keys(c.studentCodes() || {})
        if (
          currentCodes.length !== requestedStudentCodes.length ||
          !requestedStudentCodes.every((code) => currentCodes.includes(code))
        ) {
          needsClassroomRefresh = true
        }
      }

      const missingStudentCodes = requestedStudentCodes.filter(
        (code) => !studentCodes || !(code in studentCodes)
      )

      if (isNewClassroom) {
        try {
          const refreshedTeacher = await fetchTeacher()
          if (!refreshedTeacher) return

          setInternalTeacherData(refreshedTeacher)
          const updatedClientTeacher = new ClientTeacher(refreshedTeacher)
          const updatedClassroomCodes =
            updatedClientTeacher.classroomCodes(null)

          if (updatedClassroomCodes?.[classroomCode]) {
            const classroomData = await refreshClassroom(
              classroomCode,
              updatedClassroomCodes[classroomCode]
            )

            if (classroomData) {
              const { classroomStudentCodes, studentsToRefresh } =
                processClassroomData(
                  classroomData,
                  classroomCode,
                  missingStudentCodes
                )

              await refreshAndUpdateStudents(
                studentsToRefresh,
                classroomStudentCodes,
                classroomCode
              )
            }
          }
        } catch (err) {
          console.error(err)
        }
        return
      }

      if (needsClassroomRefresh && classroomCodes[classroomCode]) {
        try {
          const classroomData = await refreshClassroom(
            classroomCode,
            classroomCodes[classroomCode]
          )

          if (classroomData) {
            const { classroomStudentCodes, studentsToRefresh } =
              processClassroomData(
                classroomData,
                classroomCode,
                missingStudentCodes
              )

            if (
              studentsToRefresh.length === 0 &&
              Object.keys(classroomStudentCodes).length === 0
            ) {
              setStudentsPerClassroom((prev) => ({
                ...(prev || {}),
                [classroomCode]: [],
              }))
            } else if (studentsToRefresh.length > 0) {
              await refreshAndUpdateStudents(
                studentsToRefresh,
                classroomStudentCodes,
                classroomCode
              )
            }
          }
        } catch (err) {
          console.error(err)
        }
        return
      }

      if (missingStudentCodes.length > 0 && classroomCodes[classroomCode]) {
        try {
          const classroomData = await refreshClassroom(
            classroomCode,
            classroomCodes[classroomCode]
          )

          if (classroomData) {
            const c: ClientClassroomInstance = new ClientClassroom(
              classroomData
            )
            const classroomStudentCodes = c.studentCodes() || {}
            setStudentCodes((prev) => ({
              ...(prev || {}),
              ...classroomStudentCodes,
            }))

            const studentsToRefresh = missingStudentCodes.filter(
              (code) => classroomStudentCodes[code]
            )

            if (studentsToRefresh.length > 0) {
              await refreshAndUpdateStudents(
                studentsToRefresh,
                classroomStudentCodes,
                classroomCode
              )
            }
          }
        } catch (err) {
          console.error(err)
        }
        return
      }

      if (classrooms?.[classroomCode]) {
        try {
          const c = new ClientClassroom(classrooms[classroomCode])
          const classroomStudentCodes = c.studentCodes() || {}
          const notInMemory = Object.keys(classroomStudentCodes).filter(
            (code) => !students?.find((s) => s.code === code)
          )
          if (notInMemory.length > 0) {
            await refreshAndUpdateStudents(
              notInMemory,
              classroomStudentCodes,
              classroomCode
            )
          } else {
            const classroomStudents = Object.keys(classroomStudentCodes)
              .map((code) => students?.find((s) => s.code === code))
              .filter((s): s is ClientStudentInstance => !!s)
            setStudentsPerClassroom((prev) => ({
              ...(prev || {}),
              [classroomCode]: classroomStudents,
            }))
          }
        } catch (err) {
          console.error(err)
        }
      }
    },
    [
      classroomCodes,
      classrooms,
      studentCodes,
      students,
      fetchTeacher,
      refreshClassroom,
      processClassroomData,
      refreshAndUpdateStudents,
    ]
  )

  const updateTeacher = (prop: keyof TeacherInstance, value: any) => {
    setInternalTeacherData((prevData) => {
      if (!prevData) return null
      return { ...prevData, [prop]: value }
    })
  }

  const value = useMemo(
    () => ({
      currentTeacher: clientTeacher,
      classrooms,
      students,
      studentsPerClassroom,
      updateTeacher,
      refreshStudents,
      ensureStudentsAndClassroom,
      fetchKids,
    }),
    [
      clientTeacher,
      classrooms,
      students,
      studentsPerClassroom,
      refreshStudents,
      ensureStudentsAndClassroom,
      fetchKids,
    ]
  )

  return (
    <TeacherContext.Provider value={value}>{children}</TeacherContext.Provider>
  )
}

export default function useCurrentTeacher(): TeacherContextValue {
  const context = useContext(TeacherContext)
  if (!context) {
    throw new Error('useCurrentTeacher must be used within TeacherProvider')
  }
  return context
}
